import React, { useEffect } from 'react';
import { Form } from '@repo/ui/Form';
import { TextInputField, SelectorInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';

import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

import { gql } from '@apollo/client';

import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';
import { useFieldArray } from 'react-hook-form';

import { assertNonEmptyArray } from '@repo/util/assertNonEmptyArray';
import { Mercury } from '@repo/types/Models';
import clsx from 'clsx';
import {
  sharedSwitchConfirmation,
  type AffectedNumbers,
} from '@/features/switches/lib/sharedSwitchConfirmation';

// FORM SCHEMA

const fakeToggleOptions = [
  {
    label: 'POST',
    value: 'POST',
  },
  {
    label: 'GET',
    value: 'GET',
  },
];

const CustomParamSchema = z.object({
  name: z.string().min(1, { message: 'Parameter name must be at least 1 character.' }),
  value: z.string().min(1, { message: 'Parameter value must be at least 1 character.' }),
});

const CustomHeaderSchema = z.object({
  name: z.string().min(1, { message: 'Header name must be at least 1 character.' }),
  value: z.string().min(1, { message: 'Header value must be at least 1 character.' }),
});

const REGULAR_FIELDS = z.object({
  customParams: z.array(CustomParamSchema).optional().default([]),
  customHeaders: z.array(CustomHeaderSchema).optional().default([]),
  fakeToggle: z.string().optional(),
});

const EmailSchema = z.object({
  endpoint: z.string().email(),
});
const HttpSchema = z.object({
  endpoint: z.string().url(),
});

const blankSchema = z.object({
  endpoint: z.string().optional(),
});

const SMS_PROTOCOLS = [
  {
    label: 'No forwarding',
    value: 'NONE',
    endpoints: blankSchema,
  },
  { label: 'HTTP', value: 'HTTP', endpoints: HttpSchema },
  {
    label: 'Email',
    value: 'EMAIL',
    endpoints: EmailSchema,
  },
] as const;

const rawSmsForwardFormSchema = z.discriminatedUnion(
  'protocol',
  assertNonEmptyArray(
    SMS_PROTOCOLS.map(({ value, endpoints }) =>
      REGULAR_FIELDS.extend({
        protocol: z.literal(value),
        endpoints: endpoints,
      })
    )
  )
);

// See MmsForwardForm: Zod 4 makes `z.discriminatedUnion` incompatible with our
// `ZodObject`-only form-schema constraint, so we widen via cast.
export const updateSmsForwardFormSchema =
  rawSmsForwardFormSchema as unknown as z.ZodObject<z.ZodRawShape>;

type UpdateSmsForwardFormParams = TypeOf<typeof rawSmsForwardFormSchema>;

interface SmsForwardFormProps {
  didSwitch: Mercury.Models.Switch;
  defaultEmail?: string;
  canUpdate?: boolean;
  refetch: () => void;
  /** Other numbers share this switch: confirm before saving */
  affectedNumbers?: AffectedNumbers;
}

const SmsForwardForm = ({
  didSwitch,
  refetch,
  canUpdate,
  affectedNumbers,
}: SmsForwardFormProps) => {
  const switchId = didSwitch.id;

  const UPDATE_SMS_FORWARD = gql`
    mutation UpdateSwitch($id: ID!, $input: UpdateSwitchInput!) {
      updateSwitch(id: $id, input: $input) {
        switch {
          id
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async (rawInput: Record<string, unknown>) => {
    const input = rawInput as UpdateSmsForwardFormParams;
    const endpoint = input?.endpoints?.endpoint;

    const customHeaders = input?.customHeaders?.map(
      (header: Mercury.Models.SwitchCustomHeader) => header
    );

    const customParams = input?.customParams?.map(
      (param: Mercury.Models.SwitchCustomParam) => param
    );

    await client.mutate({
      mutation: UPDATE_SMS_FORWARD,
      variables: {
        input: {
          smsProtocol: endpoint ? input.protocol : 'NONE',
          smsEndpoints: input.protocol !== 'NONE' ? [endpoint] : [],
          smsOptions: {
            customHeaders: customHeaders,
            customParams: customParams,
          },
        },
        id: switchId,
      },
    });
    refetch();
  };

  const form = useZodForm({
    schema: updateSmsForwardFormSchema,
    onSubmit,
    confirm: () => sharedSwitchConfirmation(affectedNumbers, 'forwarding'),
    defaultValues: {
      protocol: didSwitch?.smsProtocol,
      endpoints: {
        endpoint: didSwitch?.smsEndpoints?.[0] || '',
      },
      customHeaders: didSwitch?.smsOptions?.customHeaders?.map(
        (header: Mercury.Models.SwitchCustomHeader) => header
      ),
      customParams: didSwitch?.smsOptions?.customParams?.map(
        (param: Mercury.Models.SwitchCustomParam) => param
      ),
    },
  });

  const {
    fields: customHeaderFields,
    append: appendCustomHeaderField,
    remove: removeCustomHeaderField,
  } = useFieldArray({
    name: 'customHeaders' as never,
    control: form.control,
  });

  const {
    fields: customParamFields,
    append: appendCustomParamField,
    remove: removeCustomParamField,
  } = useFieldArray({
    name: 'customParams' as never,
    control: form.control,
  });

  const protocol = form.watch('protocol');

  return (
    <Form {...form} className="flex flex-col gap-6 max-w-md">
      <Form.Error>
        Error while trying to update SMS forward. Please try again later or contact support if error
        persists.
      </Form.Error>
      <SelectorInputField
        label="Protocol"
        description='Selecting "No forwarding" or submitting no endpoints disables all forwarding. If no protocol is selected messages will not be forwarded.'
        options={SMS_PROTOCOLS}
        disabled={!canUpdate}
        value={didSwitch['smsProtocol']}
        {...registerInput(form, 'protocol')}
      />

      {protocol !== 'NONE' && (
        <>
          <div
            className={clsx(
              'flex flex-col gap-2 py-4 sm:col-span-2 border-t border-grey-400',
              protocol === 'HTTP' && 'border-b'
            )}
          >
            <div className="mb-1">
              <span className="text-sm font-bold">Endpoint</span>
            </div>
            <TextInputField
              placeholder={
                form.getValues('protocol') === 'EMAIL'
                  ? 'example@email.com'
                  : form.getValues('protocol') === 'HTTP'
                    ? 'https://example.com'
                    : 'endpoint'
              }
              label="Endpoint"
              hideLabel
              disabled={!canUpdate}
              {...registerInput(form, 'endpoints.endpoint')}
            />
          </div>

          {protocol === 'HTTP' && (
            <>
              <div className="flex flex-col gap-2 pb-4 border-b sm:col-span-2 border-grey-400">
                <div className="mb-1">
                  <span className="text-sm font-bold">Custom Headers</span>
                </div>

                {customHeaderFields.map((field, index) => {
                  return (
                    <div key={`custom-header-${index}`} className="flex gap-2 last-of-type:mb-4">
                      <TextInputField
                        placeholder="Header name"
                        label="Header name"
                        hideLabel
                        disabled={!canUpdate}
                        {...registerInput(form, `customHeaders.${index}.name`)}
                      />

                      <TextInputField
                        placeholder="Header value"
                        label="Header value"
                        hideLabel
                        disabled={!canUpdate}
                        {...registerInput(form, `customHeaders.${index}.value`)}
                      />

                      {canUpdate && (
                        <Button
                          variant="danger-secondary"
                          size="sm"
                          className="max-h-[42px]"
                          onClick={() => removeCustomHeaderField(index)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  );
                })}

                {canUpdate && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="add"
                    onClick={() => appendCustomHeaderField({ name: '', value: '' })}
                  >
                    Add Custom Header
                  </Button>
                )}
              </div>

              <div className="flex flex-col gap-2 pb-4 border-b border-grey-400 sm:col-span-2">
                <div className="mb-1">
                  <span className="text-sm font-bold">Custom Parameters</span>
                </div>

                {customParamFields.map((field, index) => {
                  return (
                    <div key={`custom-header-${index}`} className="flex gap-2 last-of-type:mb-4">
                      <TextInputField
                        placeholder="Parameter name"
                        label="Parameter name"
                        hideLabel
                        disabled={!canUpdate}
                        {...registerInput(form, `customParams.${index}.name`)}
                      />

                      <TextInputField
                        placeholder="Parameter value"
                        label="Parameter value"
                        hideLabel
                        disabled={!canUpdate}
                        {...registerInput(form, `customParams.${index}.value`)}
                      />

                      {canUpdate && (
                        <Button
                          variant="danger-secondary"
                          size="sm"
                          className="max-h-[42px]"
                          onClick={() => removeCustomParamField(index)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  );
                })}

                {canUpdate && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="add"
                    onClick={() => appendCustomParamField({ name: '', value: '' })}
                  >
                    Add Custom Parameter
                  </Button>
                )}
              </div>

              <SelectorInputField
                label="Method"
                options={fakeToggleOptions}
                disabled={!canUpdate}
                {...registerInput(form, 'fakeToggle')}
              ></SelectorInputField>
            </>
          )}
        </>
      )}

      {canUpdate && <Button type="submit">Save changes</Button>}

      <Form.Success>SMS forward sucessfully updated.</Form.Success>
    </Form>
  );
};

export default SmsForwardForm;
